<template>
  <v-dialog v-model="dialog">
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="red lighten-2" dark v-bind="attrs" v-on="on">
        Details
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline grey lighten-2">
        Area: {{ info.name || "Area name" }}
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="details">
        <base-material-card
          id="pie"
          color="primary"
          icon="mdi-chart-pie"
          width="400"
          class="mt-10 mx-3 px-4 py-3"
        >
          <template v-slot:after-heading>
            <div class="headline mt-2 font-weight-light">
              Operational resume
            </div>
          </template>
          <div v-if="operationalIsNotEmpty">
            <apexchart
              type="donut"
              :options="chartOptionsOperational"
              :series="operationalSeries"
            />
          </div>
          <div v-else class="optional">
            <v-card class="pa-10" color="secondary" dark>
              Not enough operational information (inspections) yet
            </v-card>
          </div>
        </base-material-card>
        <base-material-card
          id="pie"
          color="primary"
          icon="mdi-chart-pie"
          class="mt-10 mx-3 px-4 py-3"
          width="420"
        >
          <template v-slot:after-heading>
            <div class="mt-2 headline font-weight-light">
              Reliability resume
            </div>
          </template>
          <div v-if="reliabilityIsNotEmpty">
            <apexchart
              type="donut"
              :options="chartOptionsReliability"
              :series="reliabilitySeries"
            />
          </div>
          <div v-else class="optional">
            <v-card class="pa-10" color="secondary" dark>
              Not enough reliability information yet
            </v-card>
          </div>
        </base-material-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="dialog = false">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "PlantDetails",
  props: ["info", "type", "equipmentsPerUnit"],
  computed: {
    equipmentsReliabilityData() {
      let running = [];
      let standBy = [];
      let maintenance = [];
      let failure = [];
      const equipmentsPerUnit = this.equipmentsPerUnit;
      if (this.type === "area") {
        const { units } = this.info;
        units.forEach(function(unit) {
          if (equipmentsPerUnit[unit.name]) {
            const { equipments } = equipmentsPerUnit[unit.name];
            equipments.forEach(eq => {
              if (eq.status.name === "Running") running.push(eq);
              if (eq.status.name === "Maintenance") maintenance.push(eq);
              if (eq.status.name === "Standby") standBy.push(eq);
              if (eq.status.name === "Failure") failure.push(eq);
            });
          }
        });
      }
      return {
        running: {
          length: running.length,
          equipments: running
        },
        standBy: {
          length: standBy.length,
          equipments: standBy
        },
        maintenance: {
          length: maintenance.length,
          equipments: maintenance
        },
        failure: {
          length: failure.length,
          equipments: failure
        }
      };
    },
    reliabilitySeries() {
      if (this.equipmentsReliabilityData) {
        return Object.values(this.equipmentsReliabilityData).map(
          status => status.length
        );
      }
      return [];
    },
    equipmentsOperationalData() {
      let smooth = [];
      let warning = [];
      let alarm = [];
      const equipmentsPerUnit = this.equipmentsPerUnit;
      if (this.type === "area") {
        const { units } = this.info;
        units.forEach(function(unit) {
          if (equipmentsPerUnit[unit.name]) {
            const { equipments } = equipmentsPerUnit[unit.name];
            equipments.forEach(eq => {
              if (eq.lastInspection?.status.name === "Smooth ") smooth.push(eq);
              if (eq.lastInspection?.status.name === "Warning ")
                warning.push(eq);
              if (eq.lastInspection?.status.name === "Alarm ") alarm.push(eq);
            });
          }
        });
      }
      return {
        smooth: {
          length: smooth.length,
          equipments: smooth
        },
        warning: {
          length: warning.length,
          equipments: warning
        },
        alarm: {
          length: alarm.length,
          equipments: alarm
        }
      };
    },
    operationalSeries() {
      if (this.equipmentsOperationalData) {
        return Object.values(this.equipmentsOperationalData).map(
          status => status.length
        );
      }
      return [];
    },
    chartOptionsBasic() {
      return {
        chart: {
          background: this.$vuetify.theme.dark ? "#1E1E1E" : "#FFFFFF",
          type: "donut"
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  formatter: w => {
                    return w.globals.seriesTotals.reduce((a, b) => {
                      return a + b;
                    }, 0);
                  }
                },
                value: { formatter: val => val }
              }
            }
          }
        },
        dataLabels: {
          formatter: (_value, { w, seriesIndex }) => {
            return w.config.series[seriesIndex];
          }
        },
        theme: {
          mode: this.$vuetify.theme.dark ? "dark" : "light"
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
          offsetX: 0
        },
        tooltip: {
          y: {
            show: true,
            formatter: val => val
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                show: false
              }
            }
          }
        ]
      };
    },
    chartOptionsReliability() {
      return {
        ...this.chartOptionsBasic,
        colors: ["#008450", "#EFB700", "#DA530A", "#B81D13"],
        labels: ["Running", "Standby", "Maintenance", "Failure"]
      };
    },
    chartOptionsOperational() {
      return {
        ...this.chartOptionsBasic,
        colors: ["#008450", "#DA530A", "#B81D13"],
        labels: ["Smooth", "Warning", "Alarm"]
      };
    },
    operationalIsNotEmpty() {
      const dataSum = this.operationalSeries.reduce((a, b) => a + b, 0);
      return dataSum !== 0;
    },
    reliabilityIsNotEmpty() {
      const dataSum = this.reliabilitySeries.reduce((a, b) => a + b, 0);
      return dataSum !== 0;
    }
  },
  methods: {
    consoleThis(something) {
      console.log(something);
    }
  },
  data() {
    return {
      dialog: false
    };
  }
};
</script>
<style lang="sass" scoped>
.v-card__text
  display: flex
  flex-wrap: wrap

.optional
  display: flex
  align-items: center
  justify-content: center
  text-align: center
  min-height: 20rem
  .v-card
    width: 100%
</style>
